import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Edit, Download, Calendar, DollarSign, CheckCircle2, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { mockContracts } from '@/lib/mock-entities';
import type { ContractStatus } from '@/types/entities';

const statusConfig: Record<ContractStatus, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  draft: { label: 'Draft', variant: 'outline' },
  offer: { label: 'Offer', variant: 'secondary' },
  active: { label: 'Active', variant: 'default' },
  completed: { label: 'Completed', variant: 'secondary' },
  cancelled: { label: 'Cancelled', variant: 'destructive' },
};

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: contract, isLoading } = useQuery({
    queryKey: ['contract', id],
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 300));
      return mockContracts.find((c) => c.id === id);
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Contract Details" />
        <main className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Contract Not Found" />
        <main className="flex-1 overflow-auto p-6">
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">Contract not found</p>
            <Button onClick={() => navigate('/contracts')}>Back to Contracts</Button>
          </div>
        </main>
      </div>
    );
  }

  const paymentProgress = (contract.paidAmount / contract.totalValue) * 100;

  return (
    <div className="flex flex-col h-full">
      <Header title={contract.contractNumber}>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => console.log('Download PDF')}>
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
          <Button onClick={() => navigate(`/contracts/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Contract
          </Button>
        </div>
      </Header>

      <main className="flex-1 overflow-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium text-gray-600">Total Value</CardTitle>
              <div className="p-2 rounded-lg bg-green-100">
                <DollarSign className="h-4 w-4 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${contract.totalValue.toLocaleString()}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium text-gray-600">Paid Amount</CardTitle>
              <div className="p-2 rounded-lg bg-blue-100">
                <CheckCircle2 className="h-4 w-4 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${contract.paidAmount.toLocaleString()}</div>
              <p className="text-xs text-gray-600 mt-1">{Math.round(paymentProgress)}% paid</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium text-gray-600">Remaining</CardTitle>
              <div className="p-2 rounded-lg bg-orange-100">
                <Clock className="h-4 w-4 text-orange-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${(contract.totalValue - contract.paidAmount).toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium text-gray-600">Status</CardTitle>
              <div className="p-2 rounded-lg bg-purple-100">
                <Calendar className="h-4 w-4 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <Badge variant={statusConfig[contract.status].variant} className="text-base">
                {statusConfig[contract.status].label}
              </Badge>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contract Details */}
          <Card>
            <CardHeader>
              <CardTitle>Contract Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Client</p>
                <p className="font-medium">{contract.clientName}</p>
              </div>
              {contract.projectName && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Project</p>
                  <p className="font-medium">{contract.projectName}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600 mb-1">Contract Type</p>
                <p className="font-medium capitalize">{contract.type}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Start Date</p>
                  <p className="font-medium">{format(new Date(contract.startDate), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">End Date</p>
                  <p className="font-medium">{format(new Date(contract.endDate), 'MMM d, yyyy')}</p>
                </div>
              </div>
              {contract.signedDate && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Signed Date</p>
                  <p className="font-medium">
                    {format(new Date(contract.signedDate), 'MMM d, yyyy')}
                  </p>
                </div>
              )}
              {contract.terms && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Terms</p>
                  <p className="text-sm">{contract.terms}</p>
                </div>
              )}
              {contract.notes && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Notes</p>
                  <p className="text-sm">{contract.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Schedule */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Schedule</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {contract.paymentSchedule.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">${payment.amount.toLocaleString()}</p>
                      <p className="text-sm text-gray-600">
                        Due: {format(new Date(payment.dueDate), 'MMM d, yyyy')}
                      </p>
                      {payment.paidDate && (
                        <p className="text-sm text-green-600">
                          Paid: {format(new Date(payment.paidDate), 'MMM d, yyyy')}
                        </p>
                      )}
                    </div>
                    <Badge
                      variant={
                        payment.status === 'paid'
                          ? 'default'
                          : payment.status === 'overdue'
                            ? 'destructive'
                            : 'outline'
                      }
                    >
                      {payment.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Deliverables */}
          <Card>
            <CardHeader>
              <CardTitle>Deliverables</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {contract.deliverables.map((deliverable) => (
                  <div key={deliverable.id} className="p-3 border rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-medium">{deliverable.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{deliverable.description}</p>
                      </div>
                      <Badge
                        variant={
                          deliverable.status === 'completed'
                            ? 'default'
                            : deliverable.status === 'in-progress'
                              ? 'secondary'
                              : 'outline'
                        }
                      >
                        {deliverable.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">
                      Due: {format(new Date(deliverable.dueDate), 'MMM d, yyyy')}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Payment Progress */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-600">Payment Completion</p>
                    <p className="text-sm font-medium">{Math.round(paymentProgress)}%</p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-green-600 h-3 rounded-full transition-all"
                      style={{ width: `${Math.min(paymentProgress, 100)}%` }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-gray-600">Total Payments</p>
                    <p className="text-xl font-bold">{contract.paymentSchedule.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Completed</p>
                    <p className="text-xl font-bold">
                      {contract.paymentSchedule.filter((p) => p.status === 'paid').length}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
